import api from './api';

export const transcriptionService = {
  // Start a transcription job
  startTranscription: async (transcriptionData) => {
    try {
      const response = await api.post('/api/transcribe', {
        company_name: transcriptionData.companyName,
        quarter: transcriptionData.quarter,
        date: transcriptionData.date,
        session_id: transcriptionData.sessionId || null,
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Get transcription job status
  getTranscriptionStatus: async (jobId) => {
    try {
      const response = await api.get(`/api/transcribe_status/${jobId}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Get transcription results
  getTranscriptionResult: async (jobId) => {
    try {
      const response = await api.get(`/api/transcribed_result/${jobId}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Get transcription chunks (from in-memory jobs)
  getTranscriptionChunks: async (jobId) => {
    try {
      const response = await api.get(`/api/transcribe_chunks/${jobId}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Get transcription chunks from database (for completed jobs)
  getTranscriptionChunksFromDB: async (jobId) => {
    try {
      const response = await api.get(`/api/dashboard/transcription_chunks/${jobId}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Terminate a transcription job
  terminateTranscription: async (jobId) => {
    try {
      const response = await api.post(`/api/terminate_transcription/${jobId}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Get generated transcript by company, quarter, and date
  getGeneratedTranscript: async (searchData) => {
    try {
      const response = await api.get('/api/generated_transcript', {
        params: {
          company_name: searchData.companyName,
          quarter: searchData.quarter,
          date: searchData.date,
        },
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Validate transcription data
  validateTranscriptionData: (transcriptionData) => {
    const errors = {};

    // Validate company name
    if (!transcriptionData.companyName?.trim()) {
      errors.companyName = 'Company name is required';
    } else if (!/^[a-zA-Z0-9_\-\s]+$/.test(transcriptionData.companyName.trim())) {
      errors.companyName = 'Company name can only contain letters, numbers, spaces, hyphens, and underscores';
    }

    // Validate quarter
    const validQuarters = ['Q1', 'Q2', 'Q3', 'Q4'];
    if (!validQuarters.includes(transcriptionData.quarter?.toUpperCase())) {
      errors.quarter = 'Quarter must be Q1, Q2, Q3, or Q4';
    }

    // Validate date
    if (!transcriptionData.date) {
      errors.date = 'Date is required';
    } else if (!/^\d{4}_\d{2}_\d{2}$/.test(transcriptionData.date)) {
      errors.date = 'Date must be in YYYY_MM_DD format (e.g., 2025_09_03)';
    } else {
      try {
        const [year, month, day] = transcriptionData.date.split('_').map(Number);
        if (year < 2020 || year > 2030) {
          errors.date = 'Year must be between 2020 and 2030';
        } else if (month < 1 || month > 12) {
          errors.date = 'Month must be between 01 and 12';
        } else if (day < 1 || day > 31) {
          errors.date = 'Day must be between 01 and 31';
        }
      } catch (e) {
        errors.date = 'Invalid date values in YYYY_MM_DD format';
      }
    }

    // Validate session ID if provided
    if (transcriptionData.sessionId && !/^[a-zA-Z0-9_-]+$/.test(transcriptionData.sessionId.trim())) {
      errors.sessionId = 'Session ID can only contain letters, numbers, hyphens, and underscores';
    }

    return {
      isValid: Object.keys(errors).length === 0,
      errors,
    };
  },

  // Format date for API (YYYY_MM_DD)
  formatDateForAPI: (date) => {
    if (date instanceof Date) {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}_${month}_${day}`;
    }
    return date;
  },

  // Parse date from API format (YYYY_MM_DD)
  parseDateFromAPI: (dateString) => {
    if (!dateString || typeof dateString !== 'string') return null;
    const [year, month, day] = dateString.split('_').map(Number);
    return new Date(year, month - 1, day);
  },

  // Dashboard API methods (NEW)
  getDashboardTranscriptions: async (params = {}) => {
    try {
      const response = await api.get('/api/dashboard/transcriptions', { params });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  getDashboardSummarizations: async (params = {}) => {
    try {
      const response = await api.get('/api/dashboard/summarizations', { params });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  getDashboardStats: async () => {
    try {
      const response = await api.get('/api/dashboard/stats');
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Recording download functions
  downloadRecording: async (callId, serverName = null, baseDir = null, userId = null) => {
    try {
      const params = new URLSearchParams();
      if (serverName) params.append('server_name', serverName);
      if (baseDir) params.append('base_dir', baseDir);
      if (userId) params.append('user_id', userId);
      
      const queryString = params.toString();
      const url = `/api/recordings/download/${callId}${queryString ? '?' + queryString : ''}`;
      
      // Create a temporary link to trigger download
      const link = document.createElement('a');
      link.href = `${api.defaults.baseURL}${url}`;
      link.download = `${callId}_recording.wav`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      return { success: true, message: 'Download started' };
    } catch (error) {
      throw error;
    }
  },

  getRecordingDownloadUrl: async (callId, serverName = null, baseDir = null, userId = null) => {
    try {
      const params = new URLSearchParams();
      if (serverName) params.append('server_name', serverName);
      if (baseDir) params.append('base_dir', baseDir);
      if (userId) params.append('user_id', userId);
      
      const queryString = params.toString();
      const url = `/api/recordings/download-url/${callId}${queryString ? '?' + queryString : ''}`;
      
      const response = await api.get(url);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  checkRecordingAvailability: async (callId, serverName = null, baseDir = null, userId = null) => {
    try {
      const params = new URLSearchParams();
      if (serverName) params.append('server_name', serverName);
      if (baseDir) params.append('base_dir', baseDir);
      if (userId) params.append('user_id', userId);
      
      const queryString = params.toString();
      const url = `/api/recordings/check-availability/${callId}${queryString ? '?' + queryString : ''}`;
      
      const response = await api.get(url);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  getActiveJobs: async () => {
    try {
      const response = await api.get('/api/dashboard/active');
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  getTranscriptionDetails: async (jobId) => {
    try {
      const response = await api.get(`/api/dashboard/transcription/${jobId}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  getSummarizationDetails: async (jobId) => {
    try {
      const response = await api.get(`/api/dashboard/summarization/${jobId}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  getCompaniesList: async () => {
    try {
      const response = await api.get('/api/dashboard/companies');
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Generate call insights from completed transcription
  generateCallInsights: async (insightsData) => {
    try {
      const response = await api.post('/api/call_insights', {
        job_id: insightsData.jobId,
        knowlarity_call_id: insightsData.knowlarityCallId || null,
        max_highlights: insightsData.maxHighlights || 15,
        min_importance_score: insightsData.minImportanceScore || 6.0,
        include_sentiment_analysis: insightsData.includeSentiment !== false,
        include_topic_categorization: insightsData.includeTopics !== false,
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },
}; 